import { h, createContext } from 'preact';
import { useContext, useState } from 'preact/compat';

import {
    isCheckoutProductDataEmpty,
    parseCartProduct,
} from './gtmContext.helpers';

import {
    buildAddCartEvent,
    buildImpressionClickEvent,
    buildRefundEvent,
    buildProductDetailEvent,
    buildImpressionViewEvent,
    buildCheckoutEvent,
    buildCheckoutOptionEvent,
    buildPurchaseEvent,
} from './gtmContext.events';

// ─────────────────────────────────────────────
// GTM validation
// ─────────────────────────────────────────────

const isGTMEnabled = () => {
    try {
        const scriptTag = document.querySelector('#GTM_data');

        if (!scriptTag) throw new Error('GTM_data script is not loaded');

        const { isEnabled } = JSON.parse(scriptTag.textContent);

        if (!isEnabled) throw new Error('GTM desabilitado');

        return true;
    } catch ({ message }) {
        console.log('GTM Script:', message);
        return false;
    }
};

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────

const GtmContext = createContext(null);

export const GtmContextProvider = ({ children }) => {
    const [dataLayerProducts, setDataLayerProducts] = useState([]);

    // ── Core push ────────────────────────────

    const addEventToGTM = (event) => {
        try {
            if (!isGTMEnabled()) {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(event);
        } catch ({ message }) {
            console.error('Error while saving GTM event:', message);
        }
    };

    // ── Event handlers ───────────────────────

    const handleAddProductEvent = (product) => {
        addEventToGTM(buildAddCartEvent(product));
    };

    const handleImpressionClick = (data) => {
        addEventToGTM(buildImpressionClickEvent(data));
    };

    const handleRefundEvent = (order) => {
        addEventToGTM(buildRefundEvent(order));
    };

    const handleProductDetailEvent = (data) => {
        addEventToGTM(buildProductDetailEvent(data));
    };

    const handleImpressionViewEvent = (data, list, id) => {
        addEventToGTM(buildImpressionViewEvent(data, list, id));
    };

    const handleDataLayerCheckoutStep = ({ step, data = undefined, callback = undefined }) => {
        if (isCheckoutProductDataEmpty(data, dataLayerProducts)) return;

        addEventToGTM(buildCheckoutEvent(
            step,
            data || [...dataLayerProducts],
            callback
        ));
    };

    const handleCheckoutOptionEvent = ({ option }) => {
        addEventToGTM(buildCheckoutOptionEvent(option));
    };

    // Note: the caller is responsible for deciding whether to refund or
    // register a checkout option — that logic should not live inside handlePurchaseEvent.
    const handlePurchaseEvent = (payload) => {
        const payloadProducts = payload?.items?.products;

        if (!payloadProducts) return;

        addEventToGTM(buildPurchaseEvent(
            payload,
            payloadProducts.map(parseCartProduct)
        ));
    };

    // ── Data layer state ─────────────────────

    const hydrateDataLayerProductList = (data) => {
        if (!Array.isArray(data) || data.length === 0) return;
        setDataLayerProducts([...data]);
    };

    // ─────────────────────────────────────────
    // Context value
    // ─────────────────────────────────────────

    const value = {
        addEventToGTM,
        handleAddProductEvent,
        handleImpressionClick,
        handleRefundEvent,
        handleProductDetailEvent,
        handleImpressionViewEvent,
        handleDataLayerCheckoutStep,
        handleCheckoutOptionEvent,
        handlePurchaseEvent,
        hydrateDataLayerProductList,
    };

    return (
        <GtmContext.Provider value={value}>
            {children}
        </GtmContext.Provider>
    );
};

const useGtmContext = () => useContext(GtmContext);
export default useGtmContext;
